import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CropCategory {
  commodity: string | null;
  group: string | null;
  variety: string | null;
}

interface UserCropPlan {
  id: string;
  commodity: string | null;
  variety: string | null;
  group_name: string | null;
  season: string | null;
  year: number;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export const useCropPlan = () => {
  const [cropCategories, setCropCategories] = useState<CropCategory[]>([]);
  const [userCropPlans, setUserCropPlans] = useState<UserCropPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadCropCategories = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("Crop Categories")
      .select("*")
      .order("commodity");

    if (error) {
      console.error("Error loading crop categories:", error);
      toast({
        title: "Error",
        description: "Failed to load crop categories",
        variant: "destructive",
      });
    } else {
      setCropCategories(data || []);
    }
    setLoading(false);
  };

  const loadUserCropPlans = async () => {
    console.log("Loading user crop plans...");
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log("No user found");
      return;
    }

    console.log("User found:", user.id);

    // First get the user's profile
    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (profileError) {
      console.error("Error loading profile:", profileError);
      return;
    }

    if (!profile) {
      console.log("No profile found for user");
      return;
    }

    console.log("Profile found:", profile.id);

    // Query the user_crop_categories table
    const { data, error } = await supabase
      .from("user_crop_categories")
      .select("*")
      .eq("user_id", profile.id)
      .order("year", { ascending: false })
      .order("created_at");

    if (error) {
      console.error("Error loading user crop plans:", error);
    } else {
      console.log("Loaded crop plans:", data);
      setUserCropPlans(data || []);
    }
  };

  const addCropToPlan = async (year: number, season: string, commodity: string, variety: string, group: string) => {
    console.log("addCropToPlan called with:", { year, season, commodity, variety, group });
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log("No user found in addCropToPlan");
      return false;
    }

    console.log("User found in addCropToPlan:", user.id);

    // Get the user's profile
    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (profileError) {
      console.error("Error loading profile in addCropToPlan:", profileError);
      toast({
        title: "Error",
        description: "Profile not found",
        variant: "destructive",
      });
      return false;
    }

    if (!profile) {
      console.log("No profile found in addCropToPlan");
      toast({
        title: "Error",
        description: "Profile not found",
        variant: "destructive",
      });
      return false;
    }

    console.log("Profile found in addCropToPlan:", profile.id);

    // Check for existing crop with same details to avoid duplicates
    const { data: existingCrop, error: checkError } = await supabase
      .from("user_crop_categories")
      .select("id")
      .eq("user_id", profile.id)
      .eq("commodity", commodity)
      .eq("variety", variety || "")
      .eq("group_name", group)
      .eq("season", season)
      .eq("year", year)
      .maybeSingle();

    if (checkError) {
      console.error("Error checking for existing crop:", checkError);
    }

    if (existingCrop) {
      console.log("Crop already exists in plan");
      toast({
        title: "Already Added",
        description: "This crop is already in your plan for this season and year",
        variant: "destructive",
      });
      return false;
    }

    // Insert into user_crop_categories table
    const insertData = {
      user_id: profile.id,
      year,
      season,
      commodity,
      variety: variety || null,
      group_name: group,
      is_primary: userCropPlans.length === 0
    };

    console.log("Inserting data:", insertData);

    const { data: insertResult, error } = await supabase
      .from("user_crop_categories")
      .insert(insertData)
      .select();

    if (error) {
      console.error("Error adding crop to plan:", error);
      console.error("Error details:", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      toast({
        title: "Error",
        description: `Failed to add crop to plan: ${error.message}`,
        variant: "destructive",
      });
      return false;
    } else {
      console.log("Successfully added crop:", insertResult);
      toast({
        title: "Success",
        description: "Crop added to plan successfully",
      });
      await loadUserCropPlans();
      return true;
    }
  };

  const removeCropFromPlan = async (cropId: string) => {
    const { error } = await supabase
      .from("user_crop_categories")
      .delete()
      .eq("id", cropId);

    if (error) {
      console.error("Error removing crop from plan:", error);
      toast({
        title: "Error",
        description: "Failed to remove crop from plan",
        variant: "destructive",
      });
      return false;
    } else {
      toast({
        title: "Success",
        description: "Crop removed from plan",
      });
      loadUserCropPlans();
      return true;
    }
  };

  const setPrimaryCrop = async (cropId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get the user's profile
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (!profile) return false;

    // First, set all crops to non-primary
    const { error: updateAllError } = await supabase
      .from("user_crop_categories")
      .update({ is_primary: false })
      .eq("user_id", profile.id);

    if (updateAllError) {
      console.error("Error updating crops:", updateAllError);
      toast({
        title: "Error",
        description: "Failed to set primary crop",
        variant: "destructive",
      });
      return false;
    }

    // Then set the selected crop as primary
    const { error } = await supabase
      .from("user_crop_categories")
      .update({ is_primary: true })
      .eq("id", cropId);

    if (error) {
      console.error("Error setting primary crop:", error);
      toast({
        title: "Error",
        description: "Failed to set primary crop",
        variant: "destructive",
      });
      return false;
    } else {
      toast({
        title: "Success",
        description: "Primary crop updated",
      });
      loadUserCropPlans();
      return true;
    }
  };

  const updateCropYear = async (cropId: string, year: number) => {
    const { error } = await supabase
      .from("user_crop_categories")
      .update({ year })
      .eq("id", cropId);

    if (error) {
      console.error("Error updating crop year:", error);
      toast({
        title: "Error",
        description: "Failed to update crop year",
        variant: "destructive",
      });
      return false;
    } else {
      toast({
        title: "Success",
        description: "Crop year updated",
      });
      loadUserCropPlans();
      return true;
    }
  };

  const updateCropSeason = async (cropId: string, season: string | null) => {
    const { error } = await supabase
      .from("user_crop_categories")
      .update({ season })
      .eq("id", cropId);

    if (error) {
      console.error("Error updating crop season:", error);
      toast({
        title: "Error",
        description: "Failed to update crop season",
        variant: "destructive",
      });
      return false;
    } else {
      toast({
        title: "Success",
        description: "Crop season updated",
      });
      loadUserCropPlans();
      return true;
    }
  };

  useEffect(() => {
    loadCropCategories();
    loadUserCropPlans();
  }, []);

  return {
    cropCategories,
    userCropPlans,
    loading,
    addCropToPlan,
    removeCropFromPlan,
    setPrimaryCrop,
    updateCropYear,
    updateCropSeason,
    loadCropCategories,
    loadUserCropPlans,
  };
};
